import React, { useState } from "react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook } from "react-icons/fa";
import { Eye, EyeOff, X } from "lucide-react";
import useAuthStore from "@/stores/user/authStore";
import { toast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";

const MotionDialogContent = motion.div;
const MotionBackdrop = motion.div;

export const RateLimitModal: React.FC = () => {
  const { isOpen, clearRateLimit, message, tripName } = useRateLimitStore();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { signUp, signInWithGoogle, signInWithFacebook, isLoading } =
    useAuthStore();

  const handleClose = () => {
    clearRateLimit();
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate first name
    if (firstName.trim() === "") {
      toast({
        variant: "destructive",
        title: "Missing First Name",
        description: "Please enter your first name",
      });
      return;
    }

    // Validate last name
    if (lastName.trim() === "") {
      toast({
        variant: "destructive",
        title: "Missing Last Name",
        description: "Please enter your last name",
      });
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address",
      });
      return;
    }

    // Validate password
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/;
    if (!passwordRegex.test(password)) {
      toast({
        variant: "destructive",
        title: "Invalid Password",
        description:
          "Password must be at least 8 characters long, contain at least one number, one special character, and one capital letter",
      });
      return;
    }

    try {
      const result = await signUp(firstName, lastName, email, password);
      if (result) {
        clearRateLimit();
        navigate("/auth/callback");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      clearRateLimit();
    } catch (error) {
      console.error("Error signing in with Google", error);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      await signInWithFacebook();
      clearRateLimit();
    } catch (error) {
      console.error("Error signing in with Facebook", error);
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 10,
      transition: {
        duration: 0.2,
      },
    },
  };

  const backdropVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <AnimatePresence mode="wait">
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <MotionBackdrop
              className="fixed inset-0 bg-black/40"
              onClick={handleClose}
              aria-hidden="true"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={backdropVariants}
            />

            {/* Modal Content */}
            <MotionDialogContent
              className="sm:max-w-[500px] p-6 rounded-2xl bg-white relative z-50"
              onClick={(e) => e.stopPropagation()}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalVariants}
            >
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold text-neutral-900 text-left">
                  Get Started with{" "}
                  <span className="text-indigo-600">Waytora</span>
                </DialogTitle>
                <DialogDescription className="text-sm font-light text-neutral-600 text-left leading-6">
                  {message}{" "}
                  {tripName && (
                    <>
                      We'll save your trip{" "}
                      <span className="text-indigo-600 font-medium">
                        {tripName}
                      </span>{" "}
                      to your account.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSignup} className="mt-6 space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label
                      htmlFor="firstName"
                      className="text-sm font-light text-neutral-600"
                    >
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="h-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 text-neutral-900 font-light text-sm"
                    />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <Label
                      htmlFor="lastName"
                      className="text-sm font-light text-neutral-600"
                    >
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="h-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 text-neutral-900 font-light text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="email"
                    className="text-sm font-light text-neutral-600"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john.doe@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 text-neutral-900 font-light text-sm"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="password"
                    className="text-sm font-light text-neutral-600"
                  >
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="•••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-10 pr-10 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0 text-neutral-900 font-light text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200 rounded-lg mt-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                  ) : (
                    "Sign Up"
                  )}
                </Button>
              </form>

              <div className="mt-2 space-y-4">
                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-2/3 border-t border-neutral-200 mx-auto" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-4 text-neutral-400 font-light">
                      or continue with
                    </span>
                  </div>
                </div>

                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleGoogleSignIn}
                    className="flex justify-center items-center w-11 h-11 bg-white ring-1 ring-neutral-200 hover:bg-neutral-50 hover:shadow-sm rounded-lg transition-all duration-200"
                  >
                    <FcGoogle className="text-[24px]" />
                  </button>
                  <button
                    onClick={handleFacebookSignIn}
                    className="flex justify-center items-center w-11 h-11 bg-[#316FF6] hover:bg-blue-700 ring-1 ring-[#316FF6] hover:shadow-sm rounded-lg transition-all duration-200"
                  >
                    <FaFacebook className="text-[24px] text-white" />
                  </button>
                </div>

                <div className="flex flex-row items-center justify-center w-full">
                  <label className="ml-0.5 text-xs text-neutral-500 font-light">
                    <span>By signing up, I agree to the </span>
                    <a
                      className="text-indigo-600 hover:text-indigo-700"
                      href="#"
                    >
                      Terms and Conditions
                    </a>
                  </label>
                </div>
              </div>

              <motion.div
                className="absolute -bottom-14 left-0 right-0 flex justify-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full sm:max-w-[500px] h-12 bg-white/90 backdrop-blur-sm hover:bg-white rounded-2xl border border-neutral-200 transition-all duration-200 flex items-center justify-center"
                >
                  <span className="text-sm font-light text-neutral-600 text-left leading-6">
                    Continue as guest
                  </span>
                </button>
              </motion.div>
            </MotionDialogContent>
          </div>
        )}
      </AnimatePresence>
    </Dialog>
  );
};
